import React, {useCallback, useEffect, useMemo} from 'react';
import styled from 'styled-components';
import {Checkbox, Radio, Select} from 'antd';
import {useOutlet} from 'reconnect.js';
import * as Algorithm from 'rev.sdk.js/Utils/VariantsUtil';
import * as Cart from 'rev.sdk.js/Actions/Cart';
import InputCounter from 'rev.sdk.js/Components/InputCounter';
import * as AppActions from '../../AppActions';

const F = AppActions.F;

const VARIANTS_DISPLAY_TYPE = {
  radio: 'radio',
  select: 'select',
};

function calcOriginalPrice({amount, price, originalPrice}) {
  if (!originalPrice) {
    return amount;
  } else if (!price) {
    return amount;
  }

  const f = amount / price;
  return Math.round(originalPrice * f);
}

function ProductVariants(props) {
  const [actions] = useOutlet('actions');
  const {
    product,
    onNextConfig,
    config = null,
    disabled = false,
    rowStyle = {},
    onCartItemConfigChange,
  } = props;
  const initQuantity = useMemo(() => config?.qty || 1, [config?.qty]);
  const [quantity, setQuantity] = React.useState(initQuantity);
  const initValues = useMemo(
    () =>
      config?.variants ||
      (product.variants || []).reduce((acc, variant) => {
        if (
          variant.type === 'single' &&
          Array.isArray(variant.choices) &&
          variant.choices[0]
        ) {
          acc.push({
            name: variant.name,
            choice: variant.choices[0].name,
          });
        }
        return acc;
      }, []),
    [config?.variants, product.variants],
  );
  const [values, setValues] = React.useState(initValues);

  const fetchData = useCallback(
    async ({quantity, values}) => {
      if (onNextConfig) {
        try {
          actions.setLoading(true);
          const itemConfig = {
            qty: quantity,
            variants: values,
          };
          const resp = await Cart.calcPrice(product.id, itemConfig);
          const extra = {
            original_price: calcOriginalPrice({
              amount: resp.amount,
              price: product.price,
              originalPrice: product.original_price,
            }),
          };

          onNextConfig(itemConfig, resp, extra);
        } catch (ex) {
          console.warn(ex);
        } finally {
          actions.setLoading(false);
        }
      }
    },
    [actions, onNextConfig, product.id, product.original_price, product.price],
  );

  useEffect(() => {
    fetchData({quantity: initQuantity, values: initValues});
  }, [fetchData, initQuantity, initValues]);

  async function onConfigChange(quantity, values) {
    if (typeof onCartItemConfigChange === 'function') {
      // we're in Cart
      onCartItemConfigChange({
        qty: quantity,
        variants: values,
        extra_data: config?.extra_data,
      });
      // add extra_data to fix editCartItem overwrite it
    } else {
      // we're in Product Detail
      await fetchData({quantity, values});
    }
  }

  return (
    <Wrapper>
      {(product.variants || []).map((variant) => {
        if (variant.type === 'bool') {
          return (
            <InputField key={variant.name} style={rowStyle}>
              <Checkbox
                disabled={disabled}
                checked={Algorithm.getBool({values, name: variant.name})}
                onChange={async (evt) => {
                  const nextValues = Algorithm.setBool({
                    values,
                    setValues,
                    name: variant.name,
                    checked: evt.target.checked,
                  });
                  await onConfigChange(quantity, nextValues);
                }}>
                {F(variant, 'label')}
              </Checkbox>
            </InputField>
          );
        } else if (variant.type === 'single') {
          return (
            <InputField key={variant.name} style={rowStyle}>
              <label>{F(variant, 'label')}</label>
              {variant.displayType === VARIANTS_DISPLAY_TYPE.radio ? (
                <StyleRadioGroup
                  disabled={disabled}
                  value={Algorithm.getSingle({values, name: variant.name})}
                  buttonStyle="solid"
                  onChange={async (e) => {
                    const {value} = e.target;
                    const nextValues = Algorithm.setSingle({
                      values,
                      setValues,
                      name: variant.name,
                      choice: value,
                    });
                    await onConfigChange(quantity, nextValues);
                  }}>
                  {(variant.choices || []).map((option) => (
                    <Radio.Button
                      className="radio-btn"
                      key={option.name}
                      value={option.name}>
                      {F(option, 'label')}
                    </Radio.Button>
                  ))}
                </StyleRadioGroup>
              ) : (
                <Select
                  disabled={disabled}
                  style={{width: '100%'}}
                  value={Algorithm.getSingle({values, name: variant.name})}
                  onChange={async (value) => {
                    const nextValues = Algorithm.setSingle({
                      values,
                      setValues,
                      name: variant.name,
                      choice: value,
                    });
                    await onConfigChange(quantity, nextValues);
                  }}>
                  {(variant.choices || []).map((option) => {
                    return (
                      <Select.Option key={option.name} value={option.name}>
                        {F(option, 'label')}
                      </Select.Option>
                    );
                  })}
                </Select>
              )}
            </InputField>
          );
        }
        return null;
      })}
      <InputField style={rowStyle} className="product-quantity">
        <label>{actions.L('order_quantity')}</label>
        {disabled ? (
          <div>x {quantity}</div>
        ) : (
          <InputCounter
            step={1}
            value={quantity}
            onChange={(_value) => setQuantity(_value)}
            callback={async (_value) => {
              await onConfigChange(_value, values);
            }}
            inputProps={{
              min: 1,
              disabled,
            }}
          />
        )}
      </InputField>
    </Wrapper>
  );
}

const StyleRadioGroup = styled(Radio.Group)`
  --btn-gutter: 5px;
  width: 100%;
  display: flex !important;
  justify-content: space-between;
  flex-wrap: wrap;
  & > .radio-btn {
    margin-bottom: var(--btn-gutter);
    border-radius: 0 !important;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const InputField = styled.div`
  margin-bottom: 10px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  max-width: 260px;
  & > label {
    margin: 5px 0;
    white-space: nowrap;
  }
`;

export default ProductVariants;
